/**
 * The kinds of activity the portal knows how to show — one registry, read by
 * the lists, the routing and the editor alike.
 *
 * Modelled on `lib/adminTabs.js`, for the reason that file exists: a tab added
 * to the sidebar and not to the registry looked fine and led elsewhere. A kind
 * is added here or it does not appear at all — never halfway. An activity of
 * a kind missing from here is not shown (`kindOf` returns null).
 */
export const KINDS = {
  assignment: {
    label: "Temă",
    plural: "Teme",
    newLabel: "Temă nouă",
    submits: true,
    // What the section says under its title, and its colour: assignments are
    // work with a deadline, material is something to read — the two have to
    // be told apart at a glance (06.10.2026).
    hint: { trainer: "Ce au de predat cursanții, cu termen", participant: "Ce ai de predat, cu termen" },
    tone: "purple",
  },
  resource: {
    label: "Material",
    plural: "Materiale",
    newLabel: "Material nou",
    submits: false,
    hint: { trainer: "Ghiduri, prezentări și linkuri pentru toată grupa", participant: "De citit și de folosit, pentru toată grupa" },
    tone: "blue",
  },
};

/** A section's colours, by the kind's `tone`. */
export const KIND_TONES = {
  purple: {
    panel: "border-[#965EA7]/20 bg-[#965EA7]/[0.035]",
    badge: "bg-[#965EA7]/[0.12] text-[#7A4986]",
    icon: "bg-[#965EA7] text-white",
    rowIcon: "text-[#965EA7]",
  },
  blue: {
    panel: "border-[#3F8EBA]/20 bg-[#3F8EBA]/[0.04]",
    badge: "bg-[#3F8EBA]/[0.12] text-[#2f6d90]",
    icon: "bg-[#3F8EBA] text-white",
    rowIcon: "text-[#3F8EBA]",
  },
};

export function toneOf(kind) {
  return KIND_TONES[KINDS[kind]?.tone] || KIND_TONES.purple;
}

/** The kinds, in the order a group card shows them. */
export const KIND_ORDER = ["assignment", "resource"];

/**
 * What publishing does, under the new-item form (19.10.2026). Before the team
 * invites the participants nothing is announced one by one: the invitation
 * says how much is waiting. A material is announced when it is published, so
 * it is better published once its files are on.
 */
export function publishNote(kind, invited = true) {
  if (!invited) {
    return `Cursanții nu sunt încă invitați: ${kind === "resource" ? "îl" : "o"} văd după invitație, iar emailul de invitație `
      + "le spune câte teme și materiale îi așteaptă.";
  }
  const base = "La publicare, cursanții primesc un email, între 08 și 21.";
  return kind === "resource"
    ? `${base} Ca să nu primească un material gol, salvează-l nepublicat, adaugă fișierele și publică-l din pagina lui.`
    : base;
}

export function kindOf(activity) {
  return KINDS[activity?.kind] || null;
}

/** What a participant reads next to an activity, and the chip's tone. */
export const STATES = {
  not_started: { label: "De predat", tone: "neutral" },
  draft: { label: "Început", tone: "neutral" },
  submitted: { label: "Predat", tone: "info" },
  needs_work: { label: "De revizuit", tone: "warn" },
  accepted: { label: "Acceptat", tone: "good" },
};

export function stateOf(row) {
  if (row?.overdue && (row.state === "not_started" || row.state === "draft")) {
    return { label: "Termen depășit", tone: "late" };
  }
  return STATES[row?.state] || STATES.not_started;
}

export const TONE_CLASSES = {
  neutral: "bg-[#E7E8EB] text-[#373A45]",
  info: "bg-[#3F8EBA]/10 text-[#2f6d90]",
  warn: "bg-[#d99a2b]/15 text-[#8a5d12]",
  good: "bg-[#519872]/15 text-[#3c7457]",
  late: "bg-[#c0483f]/10 text-[#a33a32]",
};

const TZ = "Europe/Bucharest";

/** "Până pe 10 nov., 23:59" — always in Romania's time. */
export function dueLine(iso) {
  if (!iso) return "Fără termen";
  const text = new Intl.DateTimeFormat("ro-RO", {
    timeZone: TZ, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  }).format(new Date(iso));
  return `Până pe ${text}`;
}

export function whenLine(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("ro-RO", {
    timeZone: TZ, day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
  }).format(new Date(iso));
}

function partsInRomania(date) {
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  });
  return Object.fromEntries(f.formatToParts(date).map((x) => [x.type, x.value]));
}

/** Every hour for a deadline (sessions use 07–21; a deadline is often 23:59). */
export const DEADLINE_HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
export const DEADLINE_MINUTES = ["00", "15", "30", "45", "59"];
export const DEFAULT_DEADLINE_TIME = "23:59";

/** An ISO moment as the date and the time a trainer in Romania reads. */
export function toDeadlineParts(iso) {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const p = partsInRomania(d);
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

/**
 * And back: a date and a time on Romania's clock, as an ISO moment — whatever
 * zone the trainer's laptop is set to. Null without a date; 23:59 without a time.
 */
export function fromDeadlineParts(date, time) {
  if (!date) return null;
  const [y, m, d] = date.split("-").map(Number);
  const [h, mi] = (time || DEFAULT_DEADLINE_TIME).split(":").map(Number);
  if ([y, m, d, h, mi].some((n) => Number.isNaN(n))) return null;
  const wanted = Date.UTC(y, m - 1, d, h, mi);
  let ts = wanted;
  // Romania's offset at that moment (EET or EEST); twice settles a DST edge.
  for (let i = 0; i < 2; i += 1) {
    const p = partsInRomania(new Date(ts));
    ts -= Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute) - wanted;
  }
  return new Date(ts).toISOString();
}

/** Null if the link can be handed in, else why not. */
export function linkProblem(url) {
  const s = String(url || "").trim();
  if (!s) return "Lipește linkul.";
  try {
    const u = new URL(s);
    if (u.protocol !== "https:" && u.protocol !== "http:") throw new Error("scheme");
    return null;
  } catch {
    return "Merg doar linkuri care încep cu https:// sau http://.";
  }
}

/** One participant × one assignment, as the trainer's table shows it. */
export const CELLS = {
  not_started: { label: "—", tone: "neutral" },
  missing: { label: "Lipsește", tone: "late" },
  draft: { label: "Început", tone: "neutral" },
  waiting: { label: "Așteaptă feedback", tone: "info" },
  submitted: { label: "Predat", tone: "info" },
  needs_work: { label: "De revizuit", tone: "warn" },
  accepted: { label: "Acceptat", tone: "good" },
};

export function cellOf(cell) {
  return CELLS[cell?.state] || CELLS.not_started;
}

export const DECISIONS = {
  needs_work: { label: "De revizuit", hint: "Se întoarce la cursant pentru o versiune nouă.", tone: "warn" },
  accepted: { label: "Acceptat", hint: "Tema e încheiată; cursantul nu mai poate schimba nimic.", tone: "good" },
  none: { label: "Doar un comentariu", hint: "Starea rămâne cum e.", tone: "neutral" },
};

/** "de azi", "de ieri", "de 4 zile" — how long a version has waited. */
export function waitingFor(iso, now = new Date()) {
  if (!iso) return "";
  const days = Math.floor((now.getTime() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "de azi";
  if (days === 1) return "de ieri";
  return `de ${days} zile`;
}
